import { useEffect, useState } from "react";
import { Announcements } from "@/lib/api";
import { X, Megaphone } from "lucide-react";

const VISITOR_KEY = "ztb_visitor_token";

export function getVisitorToken(): string {
  try {
    let t = localStorage.getItem(VISITOR_KEY);
    if (!t) {
      t = (crypto as any).randomUUID ? (crypto as any).randomUUID()
          : Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(VISITOR_KEY, t!);
    }
    return t!;
  } catch {
    return "anonymous";
  }
}

interface Item {
  id: number;
  placement: string;
  title: string;
  content_text: string;
  content_html: string;
  image_url?: string;
  link_url?: string;
  priority: number;
  popup_frequency?: string;
}

// =================================================================
// 顶部滚动条幅（纯文本）
// =================================================================
export function AnnouncementMarquee() {
  const [items, setItems] = useState<Item[]>([]);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    Announcements.list("marquee").then((r) => setItems(r.items)).catch(() => {});
  }, []);

  if (closed || items.length === 0) return null;
  // 用全角分隔符串联多条
  const text = items.map((i) => i.content_text || i.title).filter(Boolean).join("　·　");
  if (!text) return null;

  return (
    <div className="border-b bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200">
      <div className="container mx-auto px-4 h-9 flex items-center gap-3 overflow-hidden">
        <Megaphone className="h-3.5 w-3.5 shrink-0" />
        <div className="flex-1 overflow-hidden">
          <div className="whitespace-nowrap inline-block ztb-marquee text-xs">
            {text}　{text}
          </div>
        </div>
        <button onClick={() => setClosed(true)} className="shrink-0 opacity-60 hover:opacity-100" aria-label="关闭公告">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <style>{`
        @keyframes ztb-marquee-kf {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ztb-marquee {
          animation: ztb-marquee-kf 40s linear infinite;
          padding-right: 2rem;
        }
      `}</style>
    </div>
  );
}

// =================================================================
// 弹窗公告（富文本）
// =================================================================
export function AnnouncementPopup() {
  const [items, setItems] = useState<Item[]>([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    Announcements.list("popup", getVisitorToken()).then((r) => setItems(r.items)).catch(() => {});
  }, []);

  if (items.length === 0 || idx >= items.length) return null;
  const it = items[idx]!;

  const close = async () => {
    try { await Announcements.dismiss(it.id, getVisitorToken()); } catch {}
    setIdx(idx + 1);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={close}>
      <div className="relative max-w-md w-full rounded-xl bg-card shadow-2xl overflow-hidden border" onClick={(e) => e.stopPropagation()}>
        {it.image_url && (
          <a href={it.link_url || "#"} target={it.link_url ? "_blank" : undefined} rel="noopener">
            <img src={it.image_url} alt={it.title} className="w-full max-h-72 object-cover" />
          </a>
        )}
        <div className="p-5 space-y-3">
          {it.title && <div className="text-lg font-semibold">{it.title}</div>}
          {it.content_html && (
            <div className="prose prose-sm dark:prose-invert max-w-none text-sm" dangerouslySetInnerHTML={{ __html: it.content_html }} />
          )}
          {it.link_url && (
            <a href={it.link_url} target="_blank" rel="noopener"
              className="inline-block text-sm text-primary hover:underline">
              查看详情 →
            </a>
          )}
        </div>
        <button onClick={close}
          className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center"
          aria-label="关闭">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// =================================================================
// 侧栏常驻广告（富文本）—— 桌面端右下浮动面板，可折叠
// =================================================================
export function AnnouncementSidebar() {
  const [items, setItems] = useState<Item[]>([]);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    Announcements.list("sidebar").then((r) => setItems(r.items)).catch(() => {});
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="hidden lg:block fixed right-4 bottom-4 z-30 w-64">
      {collapsed ? (
        <button onClick={() => setCollapsed(false)}
          className="ml-auto flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-2 text-xs shadow-lg hover:opacity-90">
          <Megaphone className="h-3.5 w-3.5" /> 公告 ({items.length})
        </button>
      ) : (
        <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
            <span className="inline-flex items-center gap-1"><Megaphone className="h-3 w-3" /> 站点公告</span>
            <button onClick={() => setCollapsed(true)} className="hover:text-foreground" aria-label="收起">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {items.map((it) => (
            <div key={it.id} className="rounded-lg border bg-card overflow-hidden shadow-sm">
              {it.image_url && (
                <a href={it.link_url || "#"} target={it.link_url ? "_blank" : undefined} rel="noopener">
                  <img src={it.image_url} alt={it.title} className="w-full max-h-32 object-cover" />
                </a>
              )}
              <div className="p-3 space-y-1.5">
                {it.title && <div className="text-sm font-semibold">{it.title}</div>}
                {it.content_html && (
                  <div className="prose prose-xs dark:prose-invert max-w-none text-xs" dangerouslySetInnerHTML={{ __html: it.content_html }} />
                )}
                {it.link_url && (
                  <a href={it.link_url} target="_blank" rel="noopener"
                    className="block text-xs text-primary hover:underline">
                    了解更多 →
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
